import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../shared/contexts/DataContext.js'
import config from '../../config/config.js';
import './ATCExplorer.css';
import { DataToString } from '../components/GeneralFunctions.js';
import Search from '../../shared/search/Search'
import { useNavigate } from 'react-router-dom'
import { useTableUtils } from '../../shared/contexts/TableUtilsContext'

// --- Data Transformation Logic ---
// This logic is copied from your HierarchicalViewer.js
// to make this component self-contained for ATC exploration.

const transformATCData = (data) => {
  const categories = {};

  if (!data) return categories;

  data.forEach(product => {
    const {
      atc_code_nameL1,
      atc_code_nameL2,
      atc_code_nameL3,
      atc_code_nameL4,
      atc_code_nameL5,
      active_substance_current,
      eu_pnumber,
      eu_brand_name_current,
      eu_aut_date
    } = product;

    // Ensure L1 exists, otherwise, the product can't be categorized
    if (!atc_code_nameL1) return;

    const authYear = eu_aut_date ? eu_aut_date.split('-')[0] : 'N/A';
    const productDetails = {
      active_substance_current,
      eu_pnumber,
      eu_brand_name_current,
      eu_aut_date: authYear
    };

    if (!categories[atc_code_nameL1]) {
      categories[atc_code_nameL1] = { count: 0, children: {}, products: [] };
    }
    categories[atc_code_nameL1].count += 1;

    if (atc_code_nameL2) {
      if (!categories[atc_code_nameL1].children[atc_code_nameL2]) {
        categories[atc_code_nameL1].children[atc_code_nameL2] = { count: 0, children: {}, products: [] };
      }
      categories[atc_code_nameL1].children[atc_code_nameL2].count += 1;

      if (atc_code_nameL3) {
        if (!categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3]) {
          categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3] = { count: 0, children: {}, products: [] };
        }
        categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].count += 1;

        if (atc_code_nameL4) {
          if (!categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].children[atc_code_nameL4]) {
            categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].children[atc_code_nameL4] = { count: 0, children: {}, products: [] };
          }
          categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].children[atc_code_nameL4].count += 1;

          if (atc_code_nameL5) {
            if (!categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].children[atc_code_nameL4].children[atc_code_nameL5]) {
              categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].children[atc_code_nameL4].children[atc_code_nameL5] = { count: 0, products: [] };
            }
            categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].children[atc_code_nameL4].children[atc_code_nameL5].count += 1;
            categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].children[atc_code_nameL4].children[atc_code_nameL5].products.push(productDetails);
          } else {
            categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].children[atc_code_nameL4].products.push(productDetails);
          }
        } else {
          categories[atc_code_nameL1].children[atc_code_nameL2].children[atc_code_nameL3].products.push(productDetails);
        }
      } else {
        categories[atc_code_nameL1].children[atc_code_nameL2].products.push(productDetails);
      }
    } else {
      categories[atc_code_nameL1].products.push(productDetails);
    }
  });

  return categories;
};

function capitalizeFirstLetter(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}


// --- New Redesigned TreeNode Component ---
const TreeNode = ({ name, data, level, detailsRoute }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasChildren = data.children && Object.keys(data.children).length > 0;
  const hasProducts = data.products && data.products.length > 0;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Format the name (e.g., "A - ALIMENTARY TRACT AND METABOLISM")
  const renderNodeName = () => {
    if (name.includes('-')) {
      const [firstPart, ...rest] = name.split('-');
      const secondPart = rest.join('-');
      return (
        <>
          <span className="atc-node-code">{firstPart.trim()}</span>
          <span className="atc-node-desc">{secondPart.trim()}</span>
        </>
      );
    }
    return <span className="atc-node-desc" style={{ fontWeight: 'bold' }}>{name}</span>;
  };

  return (
    <div className="atc-tree-node">
      <div
        className="atc-node-header"
        onClick={toggleExpand}
        style={{ paddingLeft: `${level * 20 + 10}px` }} // Indentation is still controlled here
        role="button"
        tabIndex={0}
        onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && toggleExpand()}
        aria-expanded={isExpanded}
        aria-label={`${name} (${data.count} items)`}
      >
        <span className="atc-node-toggle">
          {/* --- THIS IS THE ONLY JSX LINE CHANGED --- */}
          {hasChildren || hasProducts ? (isExpanded ? '−' : '+') : ''}
        </span>
        <span className="atc-node-name">
          {renderNodeName()}
        </span>
        <span className="atc-node-count">{data.count}</span>
      </div>

      {isExpanded && (
        <div className="atc-node-children">
          {/* Render Children Nodes */}
          {hasChildren &&
            Object.entries(data.children)
              .sort(([aName], [bName]) => aName.localeCompare(bName))
              .map(([childName, childData]) => (
                <TreeNode
                  key={childName}
                  name={childName}
                  data={childData}
                  level={level + 1}
                  detailsRoute={detailsRoute}
                />
              ))}

          {/* Render Products */}
          {hasProducts && (
            <ul className="atc-product-list" style={{ paddingLeft: `0px` }}>
              {/* Padding is now controlled by atc-node-children and item styles */}
              {data.products
                .sort((a, b) => a.eu_brand_name_current.localeCompare(b.eu_brand_name_current))
                .map((product, index) => (
                  <li key={product.eu_pnumber || index} className="atc-product-item">
                    <Link
                      to={`${detailsRoute}/${DataToString(product.eu_pnumber)}`}
                      className="atc-product-link"
                    >
                      {capitalizeFirstLetter(product.active_substance_current)}:
                      <span className="atc-product-brand">
                        {product.eu_brand_name_current}
                      </span>
                      ({product.eu_aut_date})
                    </Link>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};


// --- New ATC Explorer Page Component ---
const AtcExplorer = ({ config_atc = {} }) => {
  const data = useData();

  // Use useMemo to transform data only when 'data' prop changes
  const atcHierarchy = useMemo(() => transformATCData(data), [data]);

  const { tableUtils, setTableUtils } = useTableUtils()
  const navigate = useNavigate()
  const search = (query) => {
    setTableUtils({ ...tableUtils, search: query })
    if (query !== '') {
      navigate(config.routes.main.data);
    }
  }

  const {
    detailsRoute = config.routes.main.details,
    // Filter out L1 nodes that don't follow the "CODE - NAME" format
    filterFn = ([name]) => name.includes('-'),
    sortFn = (a, b) => a.localeCompare(b)
  } = config_atc;


  const filteredData = useMemo(() => {
    return Object.fromEntries(
      Object.entries(atcHierarchy).filter(filterFn)
    );
  }, [atcHierarchy, filterFn]);

  const topLevelKeys = useMemo(() => {
    return Object.keys(filteredData).sort(sortFn);
  }, [filteredData, sortFn]);


  if (!data || topLevelKeys.length === 0) {
    return (
      <div className="atc-explorer-container med-content-container">
        <h1>ATC Quick Overview</h1>
        <p>
          Explore products through the Anatomical Therapeutic Chemical (ATC) classification system.
        </p>
        <hr className="med-top-separator" />
        <p>Loading data or no ATC data available to display.</p>
      </div>
    );
  }

  return (
    <div>
    <Search update={search} />
    <div className="atc-explorer-container med-content-container">
      <h1>ATC Quick Overview</h1>
      <p>
        Explore <strong>EMRD</strong> medicinal products through the <strong>Anatomical Therapeutic Chemical (ATC)</strong> classification system,
        a system established by the World Health Organization (WHO) Collaborating Centre for Drug Statistics Methodology.
        It classifies products at five hierarchical levels, moving from broad indication to specific substance:
      </p>
      <ul>
        <li>Level 1: <strong>Anatomical</strong> main group (L - Antineoplastic and immunomodulating agents)</li>
        <li>Level 2: <strong>Therapeutic</strong> main group (L01 - Antineoplastic agents)</li>
        <li>Level 3: <strong>Pharmacological</strong> subgroup (L01A - Alkylating agents)</li>
        <li>Level 4: <strong>Chemical</strong> subgroup (L01AB - Alkyl sulphonates)</li>
        <li>Level 5: <strong>Chemical</strong> substance (L01AB01 busulfan)</li>
      </ul>
      <p>
        The classification is based on the organ or system on which they act and their chemical, pharmacological, and therapeutic properties.
        Click any category below to expand its sub-levels and begin exploring.
      </p>
      <div>
        <p>
          For more information on the ATC code system, check out the {' '}
          <a
            href="https://atcddd.fhi.no/atc_ddd_methodology/purpose_of_the_atc_ddd_system/"
            target="_blank"
            rel="noopener noreferrer"
            title={`Link to WHO ATC Purpose`}
          >
            Purpose of the ATC/DDD System
            <i class="bx bx-link-external"></i>
          </a> and the {' '}
          <a
            href="https://atcddd.fhi.no/atc/structure_and_principles/"
            target="_blank"
            rel="noopener noreferrer"
            title={`Link to WHO ATC Structure`}
          >
            Structure and principles of the ATC System
            <i class="bx bx-link-external"></i>
          </a>.
        </p>
      </div>
      <hr className="med-top-separator" />
      <div className="atc-tree-root">
        {topLevelKeys.map((name) => (
          <TreeNode
            key={name}
            name={name}
            data={filteredData[name]}
            level={0}
            detailsRoute={detailsRoute}
          />
        ))}
      </div>
    </div>
    </div>
  );
};

export default AtcExplorer;